import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { BaseModalComponent } from '../base-modal/base-modal.component';
import { ConfirmModalComponent } from '../confirm-modal/confirm-modal.component';
import { Group } from '../../../dtos/group';
import { GroupService } from '../../../services/group.service';
import { ToastService } from '../../../services/toast.service';
import { GroupUpdateRequest } from '../../../dtos/group-update-request';

@Component({
  selector: 'app-group-edit-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, BaseModalComponent, ConfirmModalComponent],
  templateUrl: './group-edit-modal.component.html',
  styleUrls: ['./group-edit-modal.component.scss'],
})
export class GroupEditModalComponent implements OnChanges {
  @Input() open = false;
  @Input() group: Group | null = null;
  @Output() openChange = new EventEmitter<boolean>();
  @Output() saved = new EventEmitter<Group>();
  @Output() deleted = new EventEmitter<string>();

  editForm: UntypedFormGroup;
  submitted = false;
  saving = false;
  deleting = false;
  showDeleteConfirm = false;
  errorMessage = '';

  constructor(
    private fb: UntypedFormBuilder,
    private groupService: GroupService,
    private toastService: ToastService,
  ) {
    this.editForm = this.fb.group({
      name: ['', [Validators.required]],
      description: [''],
      inviteActive: [false],
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['group'] && this.group) {
      this.editForm.patchValue({
        name: this.group.name,
        description: this.group.description || '',
        inviteActive: this.group.inviteActive ?? false,
      });
    }

    if (changes['open'] && this.open && this.group) {
      this.submitted = false;
      this.errorMessage = '';
      this.showDeleteConfirm = false;
      this.editForm.patchValue({
        name: this.group.name,
        description: this.group.description || '',
        inviteActive: this.group.inviteActive ?? false,
      });
    }
  }

  close(): void {
    this.open = false;
    this.submitted = false;
    this.saving = false;
    this.deleting = false;
    this.showDeleteConfirm = false;
    this.errorMessage = '';
    this.openChange.emit(false);
  }

  get deleteConfirmMessage(): string {
    const name = this.group?.name ?? 'this group';
    return `The group "${name}" will be permanently removed. This action cannot be undone.`;
  }

  askDelete(): void {
    this.showDeleteConfirm = true;
  }

  cancelDelete(): void {
    this.showDeleteConfirm = false;
  }

  confirmDelete(): void {
    if (!this.group || this.deleting) {
      return;
    }

    this.deleting = true;
    this.errorMessage = '';

    this.groupService.deleteGroup(this.group.id).subscribe({
      next: () => {
        this.toastService.success(
          'Group Deleted',
          `"${this.group!.name}" has been deleted successfully`,
        );
        this.deleted.emit(this.group!.id);
        this.close();
      },
      error: (error) => {
        console.error('Could not delete group', error);
        this.errorMessage =
          typeof error?.error === 'string'
            ? error.error
            : error?.error?.message || 'Could not delete the group. Please try again.';
        this.toastService.error('Failed to Delete Group', this.errorMessage);
        this.deleting = false;
        this.showDeleteConfirm = false;
      },
    });
  }

  save(): void {
    this.submitted = true;

    if (this.editForm.invalid || this.saving || this.deleting || !this.group) {
      return;
    }

    this.saving = true;
    this.errorMessage = '';

    const values = this.editForm.value;
    const request = new GroupUpdateRequest(
      values.name.trim(),
      values.description?.trim() || undefined,
      !!values.inviteActive,
    );

    this.groupService.updateGroup(this.group.id, request).subscribe({
      next: (updatedGroup) => {
        this.toastService.success(
          'Group Updated',
          `"${request.name}" has been updated successfully`,
        );
        this.saved.emit(updatedGroup);
        this.close();
      },
      error: (error) => {
        console.error('Could not update group', error);
        this.errorMessage =
          typeof error?.error === 'string'
            ? error.error
            : error?.error?.message || 'Could not update the group. Please try again.';
        this.toastService.error('Failed to Update Group', this.errorMessage);
        this.saving = false;
      },
    });
  }
}
